import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  Button,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  useMediaQuery,
  useTheme,
  TextField,
  InputAdornment,
  Pagination,
  SwipeableDrawer,
  IconButton,
  Badge,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CompletedIcon,
  PlayCircle as InProgressIcon,
  FiberNew as NewIcon,
  FilterListOff as FilterOffIcon,
  Search as SearchIcon,
  Timer as TimerIcon,
  Clear as ClearIcon,
  AdminPanelSettings as AdminIcon,
  Person as UserIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { courseApi, Category, MyCourse, SourceApp } from '../services/api';

interface CoursePermission {
  isAdmin: boolean;
  role: 'admin' | 'user' | null;
  hasAccess: boolean;
}

type StatusFilter = 'all' | 'new' | 'in_progress' | 'completed';

function CoursesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSkillLevel, setSelectedSkillLevel] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const ITEMS_PER_PAGE = 9;

  // Count active filters for badge
  const activeFilterCount = [
    selectedCategory,
    selectedSkillLevel,
    statusFilter !== 'all' ? statusFilter : '',
  ].filter(Boolean).length;

  // Get source filter from URL (for PM integration)
  const sourceId = searchParams.get('source');

  // Debug: Log source filter
  console.log('[CoursesPage] URL params:', {
    source: sourceId,
    fullUrl: window.location.href,
    searchString: window.location.search
  });

  // Fetch categories
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => courseApi.getCategories().then((r) => r.data),
  });

  // Fetch all courses (with optional source filter)
  const { data: coursesResponse, isLoading: coursesLoading } = useQuery({
    queryKey: ['courses', selectedCategory, sourceId],
    queryFn: async () => {
      console.log('[CoursesPage] Fetching courses with params:', {
        categorySlug: selectedCategory || undefined,
        source: sourceId || undefined,
      });
      const response = await courseApi.getCourses({
        categorySlug: selectedCategory || undefined,
        source: sourceId || undefined,
      });
      console.log('[CoursesPage] API response:', response.data?.data?.length, 'courses', response.data?.sourceApp);
      // Return the full response data including sourceApp, not just the courses array
      return response.data;
    },
  });

  // Source app info from response (when filtering by source)
  const sourceApp: SourceApp | null = coursesResponse?.sourceApp || null;

  const handleClearSourceFilter = () => {
    searchParams.delete('source');
    setSearchParams(searchParams);
  };

  // Fetch user's enrolled courses for progress info
  const { data: myCoursesResponse } = useQuery({
    queryKey: ['my-courses'],
    queryFn: () => courseApi.getMyCourses().then((r) => r.data),
  });

  const allCourses = coursesResponse?.data || [];
  const myCourses = myCoursesResponse?.data || [];

  // Create a map of course ID to enrollment info
  const enrollmentMap = new Map<string, MyCourse['enrollment']>();
  myCourses.forEach((c: MyCourse) => {
    enrollmentMap.set(c.id, c.enrollment);
  });

  // Extract unique skill levels from courses
  const skillLevels = useMemo(() => {
    const levels = new Set<string>();
    allCourses.forEach((course: any) => {
      if (course.skillLevel?.name) {
        levels.add(course.skillLevel.name);
      }
    });
    return Array.from(levels).sort();
  }, [allCourses]);

  // Filter courses based on all criteria
  const filteredCourses = useMemo(() => {
    return allCourses.filter((course: any) => {
      const enrollment = enrollmentMap.get(course.id);

      // Status filter
      let passesStatus = true;
      switch (statusFilter) {
        case 'new':
          passesStatus = !enrollment;
          break;
        case 'in_progress':
          passesStatus = !!enrollment && (enrollment.status === 'enrolled' || enrollment.status === 'in_progress');
          break;
        case 'completed':
          passesStatus = enrollment?.status === 'completed' || false;
          break;
      }
      if (!passesStatus) return false;

      // Skill level filter
      if (selectedSkillLevel && course.skillLevel?.name !== selectedSkillLevel) {
        return false;
      }

      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesTitle = course.title.toLowerCase().includes(query);
        const matchesDescription = course.description?.toLowerCase().includes(query);
        if (!matchesTitle && !matchesDescription) {
          return false;
        }
      }

      return true;
    });
  }, [allCourses, statusFilter, selectedSkillLevel, searchQuery, enrollmentMap]);

  // Pagination
  const totalPages = Math.ceil(filteredCourses.length / ITEMS_PER_PAGE);
  const paginatedCourses = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredCourses.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredCourses, currentPage]);

  // Reset page when filters change
  const handleFilterChange = () => {
    setCurrentPage(1);
  };

  const handleStartCourse = (courseId: string) => {
    navigate(`/courses/${courseId}`);
  };

  const getStatusChip = (courseId: string) => {
    const enrollment = enrollmentMap.get(courseId);
    if (!enrollment) return null;

    if (enrollment.status === 'completed') {
      return (
        <Chip
          icon={<CompletedIcon />}
          label={t('courses.completed')}
          color="success"
          size="small"
        />
      );
    }

    if (enrollment.status === 'in_progress' || enrollment.status === 'enrolled') {
      return (
        <Chip
          icon={<InProgressIcon />}
          label={`${enrollment.progressPercent}%`}
          color="primary"
          size="small"
        />
      );
    }

    return null;
  };

  if (coursesLoading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          {t('courses.title')}
        </Typography>
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('courses.title')}
      </Typography>

      {/* Source filter info banner */}
      {sourceApp && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<FilterOffIcon />}
              onClick={handleClearSourceFilter}
            >
              Alle Kurse anzeigen
            </Button>
          }
        >
          Gefiltert nach: <strong>{sourceApp.name}</strong>
        </Alert>
      )}

      {/* Search Bar - sticky on mobile */}
      <Box
        sx={{
          position: isMobile ? 'sticky' : 'relative',
          top: isMobile ? -16 : 'auto', // Offset for padding
          zIndex: isMobile ? 10 : 'auto',
          bgcolor: 'background.default',
          pt: isMobile ? 2 : 0,
          pb: 2,
          mx: isMobile ? -2 : 0,
          px: isMobile ? 2 : 0,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('courses.searchPlaceholder') || 'Kurse durchsuchen...'}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              handleFilterChange();
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchQuery('');
                      handleFilterChange();
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {/* Filter button on mobile */}
          {isMobile && (
            <Badge badgeContent={activeFilterCount} color="primary">
              <IconButton
                onClick={() => setFilterDrawerOpen(true)}
                sx={{
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <FilterIcon />
              </IconButton>
            </Badge>
          )}
        </Box>
      </Box>

      {/* Filter Drawer for Mobile */}
      {isMobile && (
        <SwipeableDrawer
          anchor="bottom"
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          onOpen={() => setFilterDrawerOpen(true)}
          disableSwipeToOpen
          PaperProps={{
            sx: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: '80vh',
            },
          }}
        >
          {/* Drag handle */}
          <Box
            sx={{
              width: 40,
              height: 4,
              bgcolor: 'grey.400',
              borderRadius: 2,
              mx: 'auto',
              my: 1.5,
            }}
          />
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Filter</Typography>
              <IconButton onClick={() => setFilterDrawerOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Status Filter in Drawer */}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Status
            </Typography>
            <ToggleButtonGroup
              value={statusFilter}
              exclusive
              onChange={(_, value) => {
                if (value) {
                  setStatusFilter(value);
                  handleFilterChange();
                }
              }}
              size="small"
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                width: '100%',
                mb: 2,
                '& .MuiToggleButton-root': {
                  flex: '1 1 45%',
                  py: 1.5,
                },
              }}
            >
              <ToggleButton value="all">{t('courses.all')}</ToggleButton>
              <ToggleButton value="new">
                <NewIcon sx={{ mr: 0.5 }} />
                Neu
              </ToggleButton>
              <ToggleButton value="in_progress">
                <InProgressIcon sx={{ mr: 0.5 }} />
                Laufend
              </ToggleButton>
              <ToggleButton value="completed">
                <CompletedIcon sx={{ mr: 0.5 }} />
                Fertig
              </ToggleButton>
            </ToggleButtonGroup>

            <Divider sx={{ my: 2 }} />

            {/* Category Filter in Drawer */}
            {categories.length > 0 && (
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>{t('courses.workArea')}</InputLabel>
                <Select
                  value={selectedCategory}
                  label={t('courses.workArea')}
                  onChange={(e) => {
                    setSelectedCategory(e.target.value);
                    handleFilterChange();
                  }}
                >
                  <MenuItem value="">{t('courses.allCategories')}</MenuItem>
                  {categories.map((cat: Category) => (
                    <MenuItem key={cat.id} value={cat.slug}>
                      {cat.name} ({cat._count.courses})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Skill Level Filter in Drawer */}
            {skillLevels.length > 0 && (
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>{t('courses.skillLevel') || 'Skill-Level'}</InputLabel>
                <Select
                  value={selectedSkillLevel}
                  label={t('courses.skillLevel') || 'Skill-Level'}
                  onChange={(e) => {
                    setSelectedSkillLevel(e.target.value);
                    handleFilterChange();
                  }}
                >
                  <MenuItem value="">{t('courses.allLevels') || 'Alle Level'}</MenuItem>
                  {skillLevels.map((level) => (
                    <MenuItem key={level} value={level}>
                      {level}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Clear filters button */}
            {activeFilterCount > 0 && (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterOffIcon />}
                onClick={() => {
                  setStatusFilter('all');
                  setSelectedCategory('');
                  setSelectedSkillLevel('');
                  handleFilterChange();
                }}
                sx={{ mt: 1 }}
              >
                Filter zurücksetzen
              </Button>
            )}

            {/* Apply button */}
            <Button
              fullWidth
              variant="contained"
              onClick={() => setFilterDrawerOpen(false)}
              sx={{ mt: 2, minHeight: 48 }}
            >
              {filteredCourses.length} Kurse anzeigen
            </Button>
          </Box>
        </SwipeableDrawer>
      )}

      {/* Desktop Filters - inline */}
      {!isMobile && (
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          {/* Status Filter */}
          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            onChange={(_, value) => {
              if (value) {
                setStatusFilter(value);
                handleFilterChange();
              }
            }}
            size="small"
          >
            <ToggleButton value="all">{t('courses.allCourses')}</ToggleButton>
            <ToggleButton value="new">
              <NewIcon sx={{ mr: 0.5 }} />
              {t('courses.notStarted')}
            </ToggleButton>
            <ToggleButton value="in_progress">
              <InProgressIcon sx={{ mr: 0.5 }} />
              {t('courses.inProgress')}
            </ToggleButton>
            <ToggleButton value="completed">
              <CompletedIcon sx={{ mr: 0.5 }} />
              {t('courses.completed')}
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Category Filter */}
          {categories.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>{t('courses.workArea')}</InputLabel>
              <Select
                value={selectedCategory}
                label={t('courses.workArea')}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  handleFilterChange();
                }}
              >
                <MenuItem value="">{t('courses.allCategories')}</MenuItem>
                {categories.map((cat: Category) => (
                  <MenuItem key={cat.id} value={cat.slug}>
                    {cat.name} ({cat._count.courses})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Skill Level Filter */}
          {skillLevels.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>{t('courses.skillLevel') || 'Skill-Level'}</InputLabel>
              <Select
                value={selectedSkillLevel}
                label={t('courses.skillLevel') || 'Skill-Level'}
                onChange={(e) => {
                  setSelectedSkillLevel(e.target.value);
                  handleFilterChange();
                }}
              >
                <MenuItem value="">{t('courses.allLevels') || 'Alle Level'}</MenuItem>
                {skillLevels.map((level) => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      )}

      {/* Results count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {filteredCourses.length} {filteredCourses.length === 1 ? 'Kurs' : 'Kurse'} gefunden
      </Typography>

      {paginatedCourses && paginatedCourses.length > 0 ? (
        <Grid container spacing={3}>
          {paginatedCourses.map((course: any) => {
            const enrollment = enrollmentMap.get(course.id);
            const isCompleted = enrollment?.status === 'completed';

            return (
              <Grid item xs={12} sm={6} md={4} key={course.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    opacity: isCompleted ? 0.85 : 1,
                    border: isCompleted ? '2px solid' : 'none',
                    borderColor: 'success.main',
                  }}
                >
                  {/* Progress bar for in-progress courses */}
                  {enrollment && enrollment.status !== 'completed' && enrollment.progressPercent > 0 && (
                    <LinearProgress
                      variant="determinate"
                      value={enrollment.progressPercent}
                      sx={{ height: 4 }}
                    />
                  )}

                  {course.thumbnailUrl ? (
                    <CardMedia
                      component="img"
                      height="140"
                      image={course.thumbnailUrl}
                      alt={course.title}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 140,
                        bgcolor: isCompleted ? 'success.light' : 'primary.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                      }}
                    >
                      {isCompleted && (
                        <CompletedIcon
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            color: 'success.dark',
                            fontSize: 32,
                          }}
                        />
                      )}
                      <Typography variant="h3" color="white">
                        {course.title.charAt(0)}
                      </Typography>
                    </Box>
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        {course.title}
                      </Typography>
                      {getStatusChip(course.id)}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {course.description?.substring(0, 100)}
                      {course.description?.length > 100 ? '...' : ''}
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {/* Permission indicator */}
                      {course.userPermission?.role === 'admin' && (
                        <Chip
                          size="small"
                          icon={<AdminIcon />}
                          label="Admin"
                          color="error"
                          variant="outlined"
                        />
                      )}
                      {course.userPermission?.role === 'user' && !course.userPermission?.isAdmin && (
                        <Chip
                          size="small"
                          icon={<UserIcon />}
                          label="Teilnehmer"
                          color="info"
                          variant="outlined"
                        />
                      )}
                      {course.workArea && (
                        <Chip size="small" label={course.workArea.name} variant="outlined" />
                      )}
                      {course.skillLevel && (
                        <Chip size="small" label={course.skillLevel.name} />
                      )}
                      {course.estimatedDurationMinutes && (
                        <Chip
                          size="small"
                          icon={<TimerIcon />}
                          label={course.estimatedDurationMinutes >= 60
                            ? `${Math.floor(course.estimatedDurationMinutes / 60)}h ${course.estimatedDurationMinutes % 60}m`
                            : `${course.estimatedDurationMinutes} Min.`}
                          variant="outlined"
                        />
                      )}
                      <Chip
                        size="small"
                        label={`${course.pointsReward} ${t('courses.points', { count: course.pointsReward }).split(' ')[1] || 'Punkte'}`}
                        color="primary"
                      />
                    </Box>
                  </CardContent>
                  <Box sx={{ p: 2, pt: 0 }}>
                    <Button
                      variant={isCompleted ? 'outlined' : 'contained'}
                      fullWidth
                      onClick={() => handleStartCourse(course.id)}
                      color={isCompleted ? 'success' : 'primary'}
                    >
                      {isCompleted
                        ? t('courses.viewDetails')
                        : enrollment
                        ? t('courses.continueCourse')
                        : t('courses.startCourse')}
                    </Button>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Alert severity="info">
          {searchQuery || selectedCategory || selectedSkillLevel || statusFilter !== 'all'
            ? 'Keine Kurse mit diesen Filterkriterien gefunden.'
            : t('courses.noCourses')}
        </Alert>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" sx={{ mt: 4, mb: 2 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, page) => setCurrentPage(page)}
            color="primary"
            size={isMobile ? 'small' : 'medium'}
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
}

export default CoursesPage;
